import { fetchAvailableRatesForOffer } from 'actions/OfferActions'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchLatestRestorableCheckoutCart } from 'actions/RecommendationActions'
import { OFFER_DYNAMIC_PRICING_TYPES } from 'constants/offer'
import { useOfferLocationsBreadcrumbs } from 'hooks/Offers/useOfferLocationBreadcrumbs'
import useOffer from 'hooks/Offers/useOffer'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'
import { isAccommodationCheckInDatePast } from 'lib/datetime/dateUtils'
import { getAccommodationPropertyTimeZone } from 'lib/datetime/accommodationPropertyTimeZone'
import { useEffect, useMemo } from 'react'
import {
  doDatesMatch,
  doTravellersMatch,
  doesDestinationMatch,
  findFirstCheckoutCartFlashHotelItem,
  getCheckoutCartFlashHotelPackageUniqueKey,
  isOneClickBookingRestorationFlashHotelOffer,
} from './utils'

interface OneClickBookingRestorationState {
  offerId?: string
  transactionKey?: string
  filters?: App.OfferListFilters
}

const EMPTY_LOCATION_BREADCRUMBS: App.LocationBreadcrumbs = { locations: [], fetching: false }

interface PackageAvailabilityOffer {
  type: App.OfferType
  property?: {
    useDynamicCancellationPolicies?: boolean
  }
}

function hasExpiredAvailabilitySchedule(offer: App.HotelOffer): boolean {
  const end = offer.availabilitySchedule?.end
  return !!end && new Date() > new Date(end)
}

function shouldFetchAvailableRates(offer: PackageAvailabilityOffer): boolean {
  return OFFER_DYNAMIC_PRICING_TYPES.includes(offer.type) || !!offer.property?.useDynamicCancellationPolicies
}

function useOneClickBookingRestoration(filters: App.OfferListFilters, enabled = true): OneClickBookingRestorationState {
  const dispatch = useAppDispatch()

  const {
    memberId,
    cart,
    cartState,
    regionCode,
    currencyCode,
    calendarsByOccupancy,
    calendarsByOccupancyLoading,
    availableRatesByOccupancy,
  } = useAppSelector((state) => ({
    memberId: state.auth.account.memberId,
    cart: state.recommendations.latestRestorableCheckoutCart.cart,
    cartState: state.recommendations.latestRestorableCheckoutCart.state,
    regionCode: state.geo.currentRegionCode,
    currencyCode: state.geo.currentCurrency,
    calendarsByOccupancy: state.calendar.calendarsByOccupancy,
    calendarsByOccupancyLoading: state.calendar.calendarsByOccupancyLoading,
    availableRatesByOccupancy: state.offer.offerAvailableRatesByOccupancy,
  }), shallowEqual)

  useEffect(() => {
    if (!enabled) return
    if (!memberId) return
    dispatch(fetchLatestRestorableCheckoutCart())
  }, [dispatch, enabled, memberId])

  const cartLoaded = cartState === 'done' || cartState === 'error'
  const item = useMemo(() => {
    if (!memberId) return undefined
    if (!enabled) return undefined
    if (!cartLoaded) return undefined
    return findFirstCheckoutCartFlashHotelItem(cart)
  }, [cart, cartLoaded, enabled, memberId])
  const [offer] = useOffer<App.AnyOffer>(item?.offerId, { disabled: !enabled || !cartLoaded || !item })
  const flashOffer = enabled && isOneClickBookingRestorationFlashHotelOffer(offer) ? offer : undefined
  const offerMetaData = useOfferMetaData(flashOffer?.id ?? '', filters)
  const isGroupedHotelOffer = !!offerMetaData?.secondaryOfferId
  const breadcrumbProperties = useMemo<Array<{ propertyId: string, type: 'le' }>>(() => (
    flashOffer?.property.id ? [{ propertyId: flashOffer.property.id, type: 'le' }] : []
  ), [flashOffer?.property.id])
  const breadcrumbs = useOfferLocationsBreadcrumbs(breadcrumbProperties, false)[0] ?? EMPTY_LOCATION_BREADCRUMBS

  const packageUniqueKey = useMemo(() => {
    if (!item) return undefined
    return getCheckoutCartFlashHotelPackageUniqueKey(item)
  }, [item])
  const pkg = useMemo(() => {
    if (!flashOffer || !packageUniqueKey) return undefined
    return flashOffer.packages.find(pkg => pkg.uniqueKey === packageUniqueKey)
  }, [flashOffer, packageUniqueKey])

  const isCartSearchMatch = useMemo(() => {
    if (!enabled) return false
    if (!item || !flashOffer) return false
    if (!doDatesMatch(filters, item)) return false
    if (!doTravellersMatch(filters, item)) return false
    return doesDestinationMatch(filters, flashOffer, breadcrumbs.locations)
  }, [breadcrumbs.locations, enabled, filters, flashOffer, item])

  useEffect(() => {
    if (!cartLoaded || !isCartSearchMatch || isGroupedHotelOffer || !item || !flashOffer || !pkg || !isHotelPackage(pkg)) return

    dispatch(fetchCalendarsByOccupancy(flashOffer.id, {
      occupants: [item.occupancy],
      packages: [pkg],
      regionCode,
      bypassRegionFilter: item.bypassRegionFilter,
    }))

    if (shouldFetchAvailableRates(flashOffer)) {
      dispatch(fetchAvailableRatesForOffer(flashOffer, {
        occupants: [item.occupancy],
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        currencyCode,
        packageIds: [item.packageId],
        bypassRegionFilter: item.bypassRegionFilter,
      }))
    }
  }, [cartLoaded, currencyCode, dispatch, flashOffer, isCartSearchMatch, isGroupedHotelOffer, item, pkg, regionCode])

  return useMemo(() => {
    if (!memberId) return {}
    if (!enabled) return {}
    if (!cartLoaded) return {}
    if (!cart?.transactionKey || !isCartSearchMatch || !item || !flashOffer) return {}
    if (isGroupedHotelOffer) return {}

    const propertyTimeZone = getAccommodationPropertyTimeZone(flashOffer)
    const isPast = isAccommodationCheckInDatePast(item.checkIn, { timeZone: propertyTimeZone })
    if (isPast || flashOffer.isSoldOut || hasExpiredAvailabilitySchedule(flashOffer)) return {}
    if (!pkg || !packageUniqueKey) return {}

    const occupancyKey = generateOccupancyStringByRoom(item.occupancy)
    const calendarsLoading = calendarsByOccupancyLoading[occupancyKey]?.[packageUniqueKey]
    const availableRateKey = buildAvailableRateKey(item.checkIn, item.checkOut, [item.occupancy])
    const availableRatesLoading = availableRatesByOccupancy[flashOffer.id]?.[availableRateKey]?.fetching
    if (calendarsLoading || availableRatesLoading) return {}

    const itemTotals = getLEPackageItemTotals(
      flashOffer,
      pkg,
      item.checkIn,
      item.checkOut,
      item.occupancy,
      undefined,
      calendarsByOccupancy,
      availableRatesByOccupancy,
    )
    if (!itemTotals?.isAvailable) return {}

    return {
      offerId: item.offerId,
      transactionKey: cart.transactionKey,
      filters: {
        ...filters,
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        rooms: [item.occupancy],
        roomRateId: item.roomRateId,
        roomTypeId: pkg.roomType.id,
        oneClickBookingRestoration: {
          packageUniqueKey,
          packagePricing: {
            price: itemTotals.price,
            memberPrice: itemTotals.memberPrice,
            value: itemTotals.value,
            memberValue: itemTotals.memberValue,
            surcharge: itemTotals.surcharge,
            extraGuestSurcharge: itemTotals.extraGuestSurcharge,
            taxesAndFees: itemTotals.taxesAndFees,
            propertyFees: itemTotals.otherFees?.propertyFees ?? 0,
          },
        },
      },
    }
  }, [
    availableRatesByOccupancy,
    calendarsByOccupancy,
    calendarsByOccupancyLoading,
    cart?.transactionKey,
    cartLoaded,
    enabled,
    filters,
    flashOffer,
    isGroupedHotelOffer,
    isCartSearchMatch,
    item,
    memberId,
    packageUniqueKey,
    pkg,
  ])
}

export default useOneClickBookingRestoration
